import React, { useEffect, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import DataTableComponent, { DataTableProps } from './DataTableComponent';
import { OrderDirection } from '../common/enums/order-direction.enum';
import { useErrorToast } from '../hooks/useToast';
import DataTableFilters from './DataTableFilters';
import Card from '../layouts/CardLayout';
import LoadingContent from './LoadingContent';
import { SelectItem } from './Select';
import CellLayout from './CellLayout';
import { ICommonRequest } from '../common/interfaces/common-request.interface';
import { CommonRequestTableBasicProps } from './CommonRequestsTableWithQueryParams';
import { useCommonRequestsQuery } from '../services/common-requests/common-requests.service';
import CommonRequestYearSelect from './YearSelect';
import CommonRequestsMonthSelect from './MonthSelect';
import { Link, useParams } from 'react-router-dom';
import PageHeader from './PageHeader';
import { useUserProfile } from '../contexts/user/UserContext';

const CommonRequestTableHeader: TableColumn<ICommonRequest>[] = [
  {
    id: 'id',
    name: 'ID',
    sortable: true,
    grow: 1,
    cell: (row: ICommonRequest) => (
      <CellLayout>
        <small id={`request_id__${row.id}`} className="cell-layout">
          {row.id}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'year',
    name: 'An',
    sortable: true,
    grow: 2,
    cell: (row: ICommonRequest) => (
      <CellLayout>
        <small id={`request_id__${row.id}`} className="cell-layout">
          {row.year}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'month',
    name: 'Luna',
    sortable: true,
    grow: 2,
    cell: (row: ICommonRequest) => (
      <CellLayout>
        <small id={`request_created-at__${row.id}`} className="cell-layout">
          {row.month}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'request_count',
    name: 'Numar de cereri',
    grow: 2,
    sortable: true,
    cell: (row: ICommonRequest) => (
      <CellLayout>
        <small id={`request_name__${row.id}`} className="cell-layout">
          {row.request_count}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'action',
    name: 'Actiuni',
    grow: 2,
    sortable: true,
    cell: (row: ICommonRequest) => (
      <Link
        to={`${row.year}/${row.month}`}
        className="btn-primary cell-layout"
        id={`request_name__${row.id}`}
      >
        Vezi cereri
      </Link>
    ),
  },
];

type CommonRequestTableType<T extends object> = React.FC<CommonRequestTableBasicProps> & {
  DataTableComponent: React.FC<DataTableProps<T>>;
};

const CommonRequestTable: CommonRequestTableType<ICommonRequest> = ({
  query,
  setQuery,
}: CommonRequestTableBasicProps) => {
  const { user } = useUserProfile();
  const { clientId } = useParams();

  const [year, setYear] = useState<SelectItem<string> | undefined>(
    query?.year ? { key: query.year, value: query.year } : undefined,
  );
  const [month, setMonth] = useState<SelectItem<string> | undefined>(
    query?.month ? { key: query.month, value: query.month } : undefined,
  );

  const {
    data: commonRequests,
    isLoading: isCommonRequestsLoading,
    error: commonRequestsError,
  } = useCommonRequestsQuery(
    clientId as string,
    user?.company_id as unknown as string,
    query?.limit as number,
    query?.page as number,
    query.year || '',
    query.month || '',
    query.orderBy,
    query.orderDirection as OrderDirection,
  );

  useEffect(() => {
    if (commonRequestsError) useErrorToast('Eroare la preluarea datelor');
  }, [commonRequestsError]);

  const onChangeRowsPerPage = (limit: number) => {
    setQuery({ limit }, 'replaceIn');
  };

  const onChangePage = (page: number) => {
    setQuery({ page }, 'replaceIn');
  };

  const onSort = (column: TableColumn<ICommonRequest>, direction: SortOrder) => {
    setQuery(
      {
        orderBy: column.id as string,
        orderDirection:
          direction.toLocaleUpperCase() === OrderDirection.ASC
            ? OrderDirection.ASC
            : OrderDirection.DESC,
      },
      'replaceIn',
    );
  };

  const onResetFilters = () => {
    setYear(undefined);
    setMonth(undefined);
    setQuery({}, 'push');
  };

  const onYearChange = (item: SelectItem<string>) => {
    setYear(item);
    setQuery({ year: item.key }, 'replaceIn');
  };

  const onMonthChange = (item: SelectItem<string>) => {
    setMonth(item);
    setQuery({ month: item.key }, 'replaceIn');
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Cereri comune" />
      <Card>
        <DataTableFilters
          onResetFilters={onResetFilters}
          filtersVisible={query?.year || query?.month ? true : false}
        >
          <CommonRequestYearSelect
            id="common_request_year_filter__select"
            onChange={onYearChange}
            selected={year}
            className="max-h-52"
          />
          <CommonRequestsMonthSelect
            id="common_request_month_filter__select"
            onChange={onMonthChange}
            selected={month}
            className="max-h-52"
          />
        </DataTableFilters>
        <CommonRequestTable.DataTableComponent
          columns={
            [
              ...CommonRequestTableHeader,
              // buildrequestActionColumn()
            ] as TableColumn<ICommonRequest>[]
          }
          pagination
          onSort={onSort}
          data={commonRequests?.items}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          paginationDefaultPage={query.page}
          paginationPerPage={query.limit}
          paginationTotalRows={commonRequests?.meta.totalItems}
          defaultSortAsc={false}
          loading={isCommonRequestsLoading}
        />
        {isCommonRequestsLoading && <LoadingContent />}
      </Card>
    </div>
  );
};

CommonRequestTable.DataTableComponent = DataTableComponent;

export default CommonRequestTable;
